import PaymentSettings from "../../components/Payments/PaymentSettings";
import EmailSettings from './EmailSettings';
import { Button, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Switch, TextField, Tooltip } from "@mui/material"
import { useEffect, useState } from "react"
import { API } from "../../lib/api"
import Feedback from "../../components/Feedback/Feedback"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import { useAuth } from "../../contexts/AuthContext";
import { useThemePreference } from "../../contexts/ThemeContext";
import { useAppSettings } from "../../contexts/AppSettingsContext";
import { writeCachedUserSettings } from "../../lib/userSettingsCache";
import "./content.css";

const AVAILABLE_THEMES = [
    { value: "default", label: "Default" },
    { value: "dark", label: "Dark" },
    { value: "glass", label: "Glass" }
];

const createMarginRow = (name = "", value = "") => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name,
    value: String(value ?? "")
});

const createConnectorRow = (name = "", url = "") => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    name,
    url
});

const toMarginRows = (forecastMargins = {}) =>
    Object.entries(forecastMargins).map(([name, value]) => ({
        ...createMarginRow(name, value)
    }));

const toConnectorRows = (apiConnectors = []) =>
    Array.isArray(apiConnectors) && apiConnectors.length
        ? apiConnectors.map((connector) => ({
            ...createConnectorRow(connector?.name || "", connector?.url || "")
        }))
        : [createConnectorRow("", "")];

const Content = ({ showApplicationSettings = false }) => {
    const [role, setRole] = useState()
    const [firstName, setFirstName] = useState()
    const [lastName, setLastName] = useState()
    const [email, setEmail] = useState()
    const [password, setPassword] = useState()
    const [newPassword, setNewPassword] = useState()
    const [feedback, setFeedback] = useState({open: false, message: "", type: ""})  
    const [showApiKey, setShowApiKey] = useState(false);
    const [canAuditAllChats, setCanAuditAllChats] = useState(false);
    const [userSettings, setUserSettings] = useState({})
    const [applicationVersion, setApplicationVersion] = useState("");
    const [maxUsers, setMaxUsers] = useState("");
    const [defaultMarginPercentage, setDefaultMarginPercentage] = useState("");
    const [marginRows, setMarginRows] = useState([]);
    const [apiConnectorRows, setApiConnectorRows] = useState([createConnectorRow("", "")]);
    const { user } = useAuth()
    const { theme, setTheme } = useThemePreference()
    const {
        applicationSettings,
        refreshApplicationSettings,
        setApplicationSettings
    } = useAppSettings();
    const canManageApplicationSettings = showApplicationSettings && user?.role === "admin";

    const handleRoleChange = (e) => {
        setRole(e.target.value)
    }

    const handleToggleApiKey = () => {
      setShowApiKey((prev) => !prev);
    };

    const handleUpdate = async () => {
        const tempData = { 
            ...(firstName && { firstName }),
            ...(lastName && { lastName }),
            ...(email && { email }),
            ...(password && {password }),
            ...(newPassword && {newPassword }),
            ...(role && {role})
        }

        const response = await API.Users.UpdateOne(user.userId, tempData)
        if (response.status === 200) {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "success" }))
        } else {
            setFeedback(prevData => ({ ...prevData, open: true, message: response.data.message, type: "error" }))
        }
    }

    const handleAuditToggle = async (checked) => {
        setCanAuditAllChats(checked);
        const response = await API.Users.UpdateOne(user.userId, { canAuditAllChats: checked });

        if (response.status === 200) {
            localStorage.setItem("user", JSON.stringify({
                ...(JSON.parse(localStorage.getItem("user") || "{}")),
                canAuditAllChats: checked
            }));
            setFeedback(prevData => ({
                ...prevData,
                open: true,
                message: response.data.message,
                type: "success"
            }));
            return;
        }

        setCanAuditAllChats(!checked);
        setFeedback(prevData => ({
            ...prevData,
            open: true,
            message: response.data.message || "Failed to update chat audit access",
            type: "error"
        }));
    };

    const handleSettingsChange = async (name, bool) => {
        let optimisticSettings = {}

        // Optimistically update UI
        setUserSettings(prev => {
            optimisticSettings = {
                ...prev,
                [name]: bool
            }
            return optimisticSettings
        });

        writeCachedUserSettings(user.userId, optimisticSettings)
    
        const response = await API.Settings.Update(user.userId, { [name]: bool });
    
        if (response.status === 200) {
            writeCachedUserSettings(user.userId, optimisticSettings)
            setFeedback(prevData => ({
                ...prevData,
                open: true,
                message: response.data.message,
                type: "success"
            }));
        } else {
            // Optionally revert the state on error
            let revertedSettings = {}
            setUserSettings(prev => {
                revertedSettings = {
                    ...prev,
                    [name]: !bool
                }
                return revertedSettings
            });
            writeCachedUserSettings(user.userId, revertedSettings)
    
            setFeedback(prevData => ({
                ...prevData,
                open: true,
                message: response.data.message || "Failed to update setting",
                type: "error"
            }));
        }
    };

    const handleThemeChange = async (nextTheme) => {
        const previousTheme = userSettings?.theme || "default";
        const optimisticSettings = {
            ...userSettings,
            theme: nextTheme
        };

        setTheme(nextTheme);
        setUserSettings(optimisticSettings);
        writeCachedUserSettings(user.userId, optimisticSettings)

        const response = await API.Settings.Update(user.userId, { theme: nextTheme });

        if (response.status === 200) {
            writeCachedUserSettings(user.userId, optimisticSettings)
            setFeedback(prevData => ({
                ...prevData,
                open: true,
                message: response.data.message,
                type: "success"
            }));
            return;
        }

        setTheme(previousTheme);
        const revertedSettings = {
            ...userSettings,
            theme: previousTheme
        };
        setUserSettings(revertedSettings);
        writeCachedUserSettings(user.userId, revertedSettings)

        setFeedback(prevData => ({
            ...prevData,
            open: true,
            message: response.data.message || "Failed to update theme",
            type: "error"
        }));
    };

    const handleMarginRowChange = (index, key, value) => {
        setMarginRows((prev) =>
            prev.map((row, rowIndex) =>
                rowIndex === index ? { ...row, [key]: value } : row
            )
        );
    };

    const handleConnectorRowChange = (index, key, value) => {
        setApiConnectorRows((prev) =>
            prev.map((row, rowIndex) =>
                rowIndex === index ? { ...row, [key]: value } : row
            )
        );
    };

    const handleSaveApplicationSettings = async () => {
        const forecastMargins = marginRows.reduce((acc, row) => {
            const name = row.name.trim();
            const value = Number(row.value);

            if (!name || Number.isNaN(value)) {
                return acc;
            }

            acc[name] = value;
            return acc;
        }, {});

        const apiConnectors = apiConnectorRows.filter((row) => row.name.trim() && row.url.trim());

        const response = await API.Settings.UpdateApplication({
            defaultMarginPercentage: Number(defaultMarginPercentage),
            forecastMargins,
            apiConnectors
        });

        if (response.status === 200) {
            const nextSettings = response.data.settings;
            setApplicationSettings(nextSettings);
            setApplicationVersion(nextSettings.applicationVersion || "");
            setMaxUsers(String(nextSettings.maxUsers ?? ""));
            setDefaultMarginPercentage(String(nextSettings.defaultMarginPercentage ?? ""));
            setMarginRows(toMarginRows(nextSettings.forecastMargins));
            setApiConnectorRows(toConnectorRows(nextSettings.apiConnectors));
            await refreshApplicationSettings();
            setFeedback(prevData => ({
                ...prevData,
                open: true,
                message: response.data.message,
                type: "success"
            }));
            return;
        }

        setFeedback(prevData => ({
            ...prevData,
            open: true,
            message: response.data?.message || "Failed to update application settings",
            type: "error"
        }));
    };

    useEffect(() => {
        if (!user) return; // 🔐 wait for user to be available
        setCanAuditAllChats(Boolean(user.canAuditAllChats));
        async function fetchSettings() {
            const response = await API.Settings.FetchByUser(user.userId)
            setUserSettings(response)
            writeCachedUserSettings(user.userId, response)
        }
        fetchSettings()
    },[user])

    useEffect(() => {
        setApplicationVersion(applicationSettings?.applicationVersion || "");
        setMaxUsers(String(applicationSettings?.maxUsers ?? ""));
        setDefaultMarginPercentage(String(applicationSettings?.defaultMarginPercentage ?? ""));
        setMarginRows(toMarginRows(applicationSettings?.forecastMargins));
        setApiConnectorRows(toConnectorRows(applicationSettings?.apiConnectors));
    }, [applicationSettings])

    return (
        <>
        <Feedback props={feedback} setFeedback={setFeedback} />
        <PaymentSettings />
        <div className="row g-3 hive-settings-page" style={{minHeight: "100vh"}}>
            <div className="col-12 col-xxl-7 d-flex">
                <div className="hive-settings-column">
                    <div className='card hive-settings-card'>
                        <div className="card-header">
                            <h5>Profile Settings</h5>
                        </div>
                        <div className="card-body hive-settings-card-body">
                            <div className="row g-4 hive-settings-field-grid hive-settings-field-grid--top">
                                <div className='col-12 col-md-6 hive-settings-field hive-settings-field--top'>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="First Name"
                                            variant="outlined"
                                            onChange={(e) => setFirstName(e.target.value)}
                                            value={firstName}
                                        />
                                    </FormControl>
                                </div>
                                <div className='col-12 col-md-6 hive-settings-field hive-settings-field--top'>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="Last Name"
                                            variant="outlined"
                                            onChange={(e) => setLastName(e.target.value)}
                                            value={lastName}
                                        />
                                    </FormControl>
                                </div>
                            </div>
                            <div className="row g-4 hive-settings-field-grid hive-settings-field-grid--top">
                                <div className='col-12 col-md-6 hive-settings-field hive-settings-field--top'>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="Email"
                                            variant="outlined"
                                            onChange={(e) => setEmail(e.target.value)}
                                            value={email}
                                        />
                                    </FormControl>
                                </div>
                                <div className='col-12 col-md-6 hive-settings-field hive-settings-field--top'>
                                    <FormControl fullWidth>
                                    <InputLabel id="role-label">Role</InputLabel>
                                        <Select
                                            labelId="role-label"
                                            id="role-select"
                                            value={role} // would be the curr user.role
                                            label="Role"
                                            onChange={handleRoleChange}
                                        >
                                            <MenuItem value={"admin"}>Admin</MenuItem>
                                            <MenuItem value={"manager"}>Manager</MenuItem>
                                            <MenuItem value={"user"}>User</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>
                            <h6 className="hive-settings-section-title">Change Password</h6>
                            <div className="row g-3 hive-settings-field-grid">
                                <div className="col-12 col-lg-6 hive-settings-field">
                                    <FormControl fullWidth>
                                        <TextField
                                            type="password"
                                            label="Current Password"
                                            variant="outlined"
                                            onChange={(e) => setPassword(e.target.value)}
                                            value={password}
                                        />
                                    </FormControl>
                                </div>
                                <div className="col-12 col-lg-6 hive-settings-field">
                                    <FormControl fullWidth>
                                        <TextField
                                            type="password"
                                            label="New Password"
                                            variant="outlined"
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            value={newPassword}
                                        />
                                    </FormControl>
                                </div>
                            </div>
                            <div className="hive-settings-actions">
                                <Button variant="outlined" onClick={handleUpdate}>Save Changes</Button>
                            </div>
                        </div>
                    </div>

                    {canManageApplicationSettings && (
                        <div className='card hive-settings-card'>
                            <div className="card-header">
                                <h5>Application Settings</h5>
                            </div>
                            <div className="card-body hive-settings-card-body">
                                <div className="hive-settings-static-row">
                                    <div>
                                        <p className="hive-settings-label">Application Version</p>
                                        <p className="hive-settings-helper-text">Current global release label used across the client.</p>
                                    </div>
                                    <span className="badge bg-light-success rounded-pill ms-2 theme-version">
                                        {applicationSettings.applicationVersion}
                                    </span>
                                </div>

                                <div className="hive-settings-divider" />
                                <div className="hive-settings-admin-grid">
                                    <TextField
                                        label="Application Version"
                                        value={applicationVersion}
                                        disabled
                                        fullWidth
                                    />
                                    <TextField
                                        label="Plan User Limit"
                                        type="number"
                                        value={maxUsers}
                                        InputProps={{ readOnly: true }}
                                        helperText="Managed by your subscription plan. Contact your system administrator to upgrade."
                                        fullWidth
                                    />
                                    <TextField
                                        label="Default Margin %"
                                        type="number"
                                        value={defaultMarginPercentage}
                                        onChange={(e) => setDefaultMarginPercentage(e.target.value)}
                                        fullWidth
                                    />
                                </div>

                                <div className="hive-settings-subsection">
                                    <div className="hive-settings-subsection-header">
                                        <div>
                                            <p className="hive-settings-label">Forecast Margins By Product Group</p>
                                            <p className="hive-settings-helper-text">These values drive new and updated revenue-line margin calculations.</p>
                                        </div>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setMarginRows((prev) => [...prev, createMarginRow("", "")])}
                                        >
                                            Add Margin
                                        </Button>
                                    </div>

                                    <div className="hive-settings-list">
                                        {marginRows.map((row, index) => (
                                            <div className="hive-settings-inline-grid" key={row.id}>
                                                <TextField
                                                    label="Product Group"
                                                    value={row.name}
                                                    onChange={(e) => handleMarginRowChange(index, "name", e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Margin %"
                                                    type="number"
                                                    value={row.value}
                                                    onChange={(e) => handleMarginRowChange(index, "value", e.target.value)}
                                                    fullWidth
                                                />
                                                <Button
                                                    color="error"
                                                    onClick={() => setMarginRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index))}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="hive-settings-subsection">
                                    <div className="hive-settings-subsection-header">
                                        <div>
                                            <p className="hive-settings-label">API Connector URLs</p>
                                            <p className="hive-settings-helper-text">Store named integration endpoints without shipping new client code.</p>
                                        </div>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => setApiConnectorRows((prev) => [...prev, createConnectorRow("", "")])}
                                        >
                                            Add Connector
                                        </Button>
                                    </div>

                                    <div className="hive-settings-list">
                                        {apiConnectorRows.map((row, index) => (
                                            <div className="hive-settings-inline-grid hive-settings-inline-grid--wide" key={row.id}>
                                                <TextField
                                                    label="Connector Name"
                                                    value={row.name}
                                                    onChange={(e) => handleConnectorRowChange(index, "name", e.target.value)}
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Connector URL"
                                                    value={row.url}
                                                    onChange={(e) => handleConnectorRowChange(index, "url", e.target.value)}
                                                    fullWidth
                                                />
                                                <Button
                                                    color="error"
                                                    onClick={() => setApiConnectorRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index))}
                                                >
                                                    Remove
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="hive-settings-actions">
                                    <Button variant="contained" onClick={handleSaveApplicationSettings}>
                                        Save Application Settings
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="col-12 col-xxl-5">
                <div className='card hive-settings-card hive-settings-stack-card'>
                    <div className="card-header">
                        <h5>User Settings</h5>
                    </div>
                    <div className="card-body hive-settings-card-body">
                        <section className="hive-settings-block">
                            <h6 className="hive-settings-section-title hive-settings-section-title--compact">Email Settings</h6>
                            <EmailSettings />
                        </section>

                        <div className="hive-settings-divider" />

                        <section className="hive-settings-block">
                            <h6 className="hive-settings-section-title hive-settings-section-title--compact">Global Settings</h6>
                            <div className="hive-settings-toggle-row">
                                <div>
                                    <p className="hive-settings-label">Speed Dial</p>
                                    <p className="hive-settings-helper-text">Create an Opportunity, Customer, or Contact from anywhere.</p>
                                </div>
                                <Tooltip title="Create an Opportunity, Customer, or Contact from anywhere">
                                    <Switch
                                        onChange={(e) => handleSettingsChange("speedDial", e.target.checked) }
                                        checked={Boolean(userSettings?.speedDial)}
                                    />
                                </Tooltip>
                            </div>

                            <div className="hive-settings-divider" />
                            <h6 className="hive-settings-section-title hive-settings-section-title--compact">Theme</h6>
                            <div className="hive-settings-select-row">
                                <div>
                                    <p className="hive-settings-label">Current Theme</p>
                                    <p className="hive-settings-helper-text">Choose between the default, dark, and Glass themes.</p>
                                </div>
                                <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
                                    <InputLabel id="theme-select-label">Theme</InputLabel>
                                    <Select
                                        labelId="theme-select-label"
                                        value={userSettings?.theme || theme || "default"}
                                        label="Theme"
                                        onChange={(e) => handleThemeChange(e.target.value)}
                                    >
                                        {AVAILABLE_THEMES.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>

                            {canManageApplicationSettings && (
                                <>
                                    <div className="hive-settings-toggle-row">
                                        <div>
                                            <p className="hive-settings-label">Audit All Chats</p>
                                            <p className="hive-settings-helper-text">Receive visibility and unread chat updates for every internal chat.</p>
                                        </div>
                                        <Tooltip title="Admin-only chat oversight access">
                                            <Switch
                                                onChange={(e) => handleAuditToggle(e.target.checked)}
                                                checked={canAuditAllChats}
                                            />
                                        </Tooltip>
                                    </div>
                                </>
                            )}
                        </section>

                        <div className="hive-settings-divider" />

                        <section className="hive-settings-block">
                            <h6 className="hive-settings-section-title hive-settings-section-title--compact">API Settings</h6>
                            <div className="hive-settings-api-row">
                                <div className="hive-settings-api-copy">
                                    <p className="hive-settings-label">API Key</p>
                                    <p className="hive-settings-helper-text">Use the visibility toggle to reveal or hide your current key.</p>
                                </div>
                                <FormControl fullWidth>
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        value={user?.apiKey ? user.apiKey : ""}
                                        type={showApiKey ? "text" : "password"}
                                        InputProps={{
                                            readOnly: true,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleToggleApiKey} edge="end">
                                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                            </div>
                        </section>
                    </div>
                </div>
            </div>
        </div>
        </>
    )
}

export default Content
